import { useMutation } from '@apollo/client';
import { Button, FluentProvider, webLightTheme, Spinner } from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  DELETE_MILESTONE_CATEGORY,
  GET_PROCESS_TYPE_CATEGORIES,
} from '../../../../utils/statApi/MilestoneTemplateApi';
import DeleteConfirmationDialog from '../../../common/DeleteConfirmationDialog';
import LoadingErrorMessage from '../../../common/errorContent/LoadingErrorMessage';
import { IProcessTemplateTypeParams } from '../../../common/ParamTypes';
import { iconButtonStyles } from '../../../../app/common/styles/CommonStyleObjects';

interface IMilestoneCategoriesDeleteButtonProps {
  id: number;
}

const MilestoneCategoriesDeleteButton = ({
  id,
}: IMilestoneCategoriesDeleteButtonProps): JSX.Element => {
  const [isDeleteConfirmHidden, setIsDeleteConfirmHidden] = useState(true);
  const [error, setError] = useState(null);

  const hideDeleteDialog = (): void => {
    setIsDeleteConfirmHidden(true);
    setError(null);
  };

  const { processTypeId } = useParams<IProcessTemplateTypeParams>();
  const processTypeIdInt = parseInt(processTypeId, 10);
  const [deleteCategory, { loading }] = useMutation(DELETE_MILESTONE_CATEGORY, {
    onCompleted: () => {
      hideDeleteDialog();
    },
    refetchQueries: [
      {
        query: GET_PROCESS_TYPE_CATEGORIES,
        variables: { processTypeId: processTypeIdInt },
      },
    ],
    onError: setError,
  });

  const deleteButtonClick = (): void => {
    setIsDeleteConfirmHidden(false);
  };

  const deleteConfirmClick = (): void => {
    deleteCategory({
      variables: {
        milestoneCategoryId: id,
      },
    });
  };

  return (
    <>
      <Button
        appearance="transparent"
        aria-label="Delete Category"
        icon={<DeleteRegular color={iconButtonStyles.color} />}
        onClick={deleteButtonClick}
      >
        Delete Category
      </Button>
      <FluentProvider theme={webLightTheme}>
        <DeleteConfirmationDialog
          open={!isDeleteConfirmHidden}
          message="Are you sure you want to delete this milestone category?"
          isConfirming={loading}
          onConfirm={deleteConfirmClick}
          onCancel={hideDeleteDialog}
        >
          <LoadingErrorMessage error={error} loading={loading} />
          {loading && <Spinner aria-label="Loading" />}
        </DeleteConfirmationDialog>
      </FluentProvider>
    </>
  );
};

export default MilestoneCategoriesDeleteButton;
